/**
 * Aufgaben und Checklisten (E12): fällig, überfällig, wiederkehrend.
 *
 * Eine Regel für App und Scheduler: Was die App als „heute fällig" zeigt, ist
 * das, wozu der Scheduler erinnert.
 */

import { addDays } from './time.js';
import type { IsoDate } from './time.js';

export const REPEAT_UNITS = ['day', 'week', 'month'] as const;
export type RepeatUnit = (typeof REPEAT_UNITS)[number];

/** „Alle n Tage/Wochen/Monate", gerechnet ab dem Fälligkeitstag, nicht ab dem Erledigen. */
export interface Repeat {
  every: number;
  unit: RepeatUnit;
}

export const TASK_LIST_KINDS = ['tasks', 'checklist'] as const;
export type TaskListKind = (typeof TASK_LIST_KINDS)[number];

export function isRepeat(value: unknown): value is Repeat {
  if (!value || typeof value !== 'object') return false;
  const { every, unit } = value as { every?: unknown; unit?: unknown };
  return (
    typeof every === 'number' &&
    Number.isInteger(every) &&
    every >= 1 &&
    every <= 365 &&
    typeof unit === 'string' &&
    (REPEAT_UNITS as readonly string[]).includes(unit)
  );
}

/** Monate addieren — der 31. wird im kürzeren Monat zum letzten Tag, nicht zum Monatsersten danach. */
function addMonthsClamped(date: string, months: number): string {
  const [y, m, d] = date.split('-').map(Number);
  const total = y! * 12 + (m! - 1) + months;
  const year = Math.floor(total / 12);
  const month = (total % 12) + 1;
  const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return `${year}-${String(month).padStart(2, '0')}-${String(Math.min(d!, last)).padStart(2, '0')}`;
}

/**
 * Nächster Fälligkeitstag nach dem Erledigen.
 *
 * Gerechnet vom alten Fälligkeitstag aus, bis er nach `today` liegt — wer den
 * Müll drei Wochen lang vergessen hat, bekommt nicht drei überfällige
 * Wiederholungen, sondern die nächste kommende.
 */
export function nextDueDate(dueDate: string, repeat: Repeat, today: string): string {
  let next = dueDate;
  for (let guard = 0; guard < 2000; guard += 1) {
    next =
      repeat.unit === 'day'
        ? addDays(next as IsoDate, repeat.every)
        : repeat.unit === 'week'
          ? addDays(next as IsoDate, repeat.every * 7)
          : addMonthsClamped(next, repeat.every);
    if (next > today) return next;
  }
  return next;
}

export type TaskState = 'done' | 'overdue' | 'today' | 'upcoming' | 'someday';

export function taskState(
  task: { doneAt: string | null; dueDate: string | null },
  today: string,
): TaskState {
  if (task.doneAt) return 'done';
  if (!task.dueDate) return 'someday';
  if (task.dueDate < today) return 'overdue';
  if (task.dueDate === today) return 'today';
  return 'upcoming';
}
